import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMobileNav } from "../context/MobileNavContext";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BrandMark from "./BrandMark";
import {
    Terminal, MagnifyingGlass, SignIn, SignOut, Plus,
    BookmarkSimple, ShieldCheck, Gear, Lightning, List, Briefcase, Users,
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import DMNavButton from "./DMNavButton";
import SearchModal from "./SearchModal";
import { PublicNavDesktop, PublicNavTabletRow, PublicNavMobile } from "./PublicNav";
import CommandPalette from "./CommandPalette";
import { API, api } from "../lib/api";

function resolvePic(p) {
    if (!p) return null;
    return p.startsWith("http") ? p : `${API}${p.replace(/^\/api/, "")}`;
}

export default function TopNav({ onOpenAI }) {
    // iter222c — the brand mark follows the audience's color theme on the
    // segment landing pages: recruiters=violet, companies=emerald, default cyan.
    const { pathname, search } = useLocation();
    const brandTone = (pathname.startsWith("/for-recruiters") || pathname.startsWith("/recruiter"))
        ? { tone: "violet", box: "bg-violet-400 group-hover:bg-violet-300", text: "text-violet-300", border: "border-violet-400" }
        : (pathname.startsWith("/for-companies") || pathname.startsWith("/company") || (pathname === "/pricing" && search.includes("for=companies")))
            ? { tone: "emerald", box: "bg-emerald-400 group-hover:bg-emerald-300", text: "text-emerald-300", border: "border-emerald-400" }
            : { tone: "cyan", box: "bg-cyan-400 group-hover:bg-cyan-300", text: "text-cyan-400", border: "border-cyan-400" };
    const { user, logout } = useAuth();
    const [searchOpen, setSearchOpen] = useState(false);
    // Amber dot on the account avatar while a crypto payment awaits confirmation.
    const [hasPendingCrypto, setHasPendingCrypto] = useState(false);
    useEffect(() => {
        if (!user || user.account_type === "recruiter") { setHasPendingCrypto(false); return; }
        let cancelled = false;
        api.get("/crypto/orders/pending-count")
            .then(({ data }) => { if (!cancelled) setHasPendingCrypto((data?.pending || 0) > 0); })
            .catch(() => { /* dot just won't show */ });
        return () => { cancelled = true; };
    }, [user, pathname]);
    const { toggle: toggleMobileNav, open: mobileNavOpen } = useMobileNav();
    const { t } = useTranslation();

    // Cmd+K / Ctrl+K opens search modal anywhere on the app
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen(true);
            }
            if (e.key === "Escape") setSearchOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <header className="sticky top-0 z-40 bg-[#05070a]/85 backdrop-blur-xl border-b border-white/[0.07]" data-testid="top-nav">
            <a href="#main-content" data-testid="skip-to-content"
               className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-cyan-400 focus:text-slate-950 focus:text-xs focus:font-bold focus:px-3 focus:py-1.5 focus:rounded-sm">
                skip to main content
            </a>
            <div className="w-full px-3 sm:px-6 lg:px-10 xl:px-14 flex items-center gap-2 sm:gap-3 h-16">
                <Link
                    to="/"
                    onClick={(e) => {
                        // Logged-in users on mobile/tablet (< lg): the logo doubles as the sidebar toggle.
                        // Logged-out users have no sidebar, so the logo always navigates to `/` (landing).
                        if (user && typeof window !== "undefined" && window.innerWidth < 1024) {
                            e.preventDefault();
                            toggleMobileNav();
                        }
                    }}
                    className="flex items-center gap-1.5 min-[360px]:gap-3 group"
                    data-testid="logo-link"
                    aria-expanded={user ? mobileNavOpen : undefined}
                    aria-controls={user ? "sidebar-mobile" : undefined}
                    aria-label={user ? "hacking.community — tap to open menu on mobile" : "hacking.community — home"}
                >
                    <BrandMark
                        tone={brandTone.tone}
                        tagline="play in the sandbox."
                        hideWordOnMobile={!!user}
                        badge={user ? <List size={9} weight="bold" className={`lg:hidden absolute -top-1 -right-1 bg-slate-950 ${brandTone.text} rounded-full p-0.5 box-content border ${brandTone.border}`} aria-hidden="true" /> : null}
                    />
                </Link>

                {/* Signed-out visitors have no sidebar — six-category primary nav (desktop inline). */}
                {!user && <PublicNavDesktop />}

                {/* Tabs removed — duplicates the sidebar nav. The left sidebar (desktop) and the
                    hamburger drawer (mobile) carry the same destinations. */}

                {user && user.account_type !== "recruiter" && (
                <button
                    onClick={() => setSearchOpen(true)}
                    className="ml-auto md:mr-1 flex items-center justify-center shrink-0 w-11 h-11 sm:w-9 sm:h-9 text-slate-400 hover:text-cyan-300 hover:bg-white/5 rounded-lg transition-colors"
                    data-testid="search-trigger"
                    title={t("common.search_aria")}
                    aria-label={t("common.search")}
                >
                    <MagnifyingGlass size={18} weight="bold" />
                </button>
                )}
                {/* iter146: recruiters don't search hacker content. Push the
                    right-side cluster to the far right when search is hidden. */}
                {user && user.account_type === "recruiter" && <div className="ml-auto" />}

                {user && user.account_type !== "recruiter" && (
                <button
                    onClick={onOpenAI}
                    className="hidden sm:flex items-center gap-1.5 h-9 text-[11px] font-semibold uppercase tracking-widest text-purple-300 hover:text-purple-200 px-3 rounded-lg bg-purple-500/10 hover:bg-purple-500/15 ring-1 ring-purple-500/25 transition-colors whitespace-nowrap shrink-0"
                    data-testid="open-ai-helper-btn"
                    title={t("nav.ai_helper")}
                >
                    <Lightning size={14} weight="fill" /> SEC-OPS
                </button>
                )}

                {/* For logged-out users push the Join Now button to the far right */}
                {!user && <div className="ml-auto" />}

                {user ? (
                    <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                        {/* iter146: DM (E2E chat) is a hacker peer-to-peer feature.
                            Recruiters use templated outreach + inbox threads instead. */}
                        {user.account_type !== "recruiter" && <DMNavButton />}
                        <NotificationBell />
                        {/* iter146: bookmarks are Q&A / article saves — irrelevant for recruiters. */}
                        {user.account_type !== "recruiter" && (
                            <Link to="/bookmarks" className="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-white/5 shrink-0 transition-colors" data-testid="bookmarks-link" title="Bookmarks">
                                <BookmarkSimple size={18} weight="duotone" />
                            </Link>
                        )}
                        {(user.role === "admin" || user.role === "mod") && (
                            <Link to="/admin" className="hidden sm:flex items-center gap-1.5 h-9 text-[11px] font-semibold uppercase tracking-widest text-cyan-300 hover:text-cyan-200 px-3 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/15 ring-1 ring-cyan-500/25 whitespace-nowrap shrink-0 transition-colors" data-testid="admin-link" title="Mod console">
                                <ShieldCheck size={14} weight="duotone" /> {t("nav.mod_console")}
                            </Link>
                        )}
                        {user.account_type === "recruiter" ? (
                            <>
                                {/* iter140: recruiter chrome — primary CTAs are
                                    Find Talent + Post Job, not Ask Question.
                                    Hidden on mobile to fit the bell + DM icons. */}
                                <Link to="/recruiter/talent" data-testid="recruiter-find-talent-btn" className="shrink-0 hidden md:block">
                                    <Button variant="outline" className="bg-transparent border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-sm h-9 px-3 text-xs uppercase tracking-wider gap-1 ml-1 whitespace-nowrap">
                                        <Users size={14} weight="duotone" /> Find talent
                                    </Button>
                                </Link>
                                <Link to="/recruiter/jobs/new" data-testid="recruiter-post-job-btn" className="shrink-0">
                                    <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold rounded-sm h-9 px-3 text-xs uppercase tracking-wider gap-1 ml-1 whitespace-nowrap">
                                        <Plus size={14} weight="bold" /> Post job
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <Link to="/ask" data-testid="ask-link"
                                  className="shrink-0 inline-flex items-center gap-1 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold rounded-lg h-9 px-2.5 sm:px-3.5 text-xs uppercase tracking-wider ml-0.5 whitespace-nowrap transition-colors">
                                <Plus size={14} weight="bold" /> {t("nav.ask")}
                            </Link>
                        )}
                        <Link to={user.account_type === "recruiter" ? "/recruiter" : `/u/${user.username}`} className="flex items-center gap-2 h-9 px-0 sm:pl-1 sm:pr-2 hover:bg-white/5 rounded-lg ml-0.5 shrink-0 transition-colors" data-testid="profile-link">
                            <div className="relative w-9 h-9 bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center rounded-lg text-[10px] font-bold text-slate-950 overflow-hidden shrink-0">
                                {resolvePic(user.picture) ? <img loading="lazy" decoding="async" src={resolvePic(user.picture)} alt="" className="w-full h-full object-cover" /> : user.username[0].toUpperCase()}
                                {hasPendingCrypto && (
                                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[#05070a] animate-pulse"
                                          data-testid="nav-pending-crypto-dot" title="Crypto payment awaiting confirmation" />
                                )}
                            </div>
                            <div className="hidden lg:block leading-tight whitespace-nowrap">
                                <div className="text-xs font-medium text-slate-200">{user.username}</div>
                                {user.account_type === "recruiter" ? (
                                    <div className="text-[10px] text-emerald-400 uppercase tracking-widest flex items-center gap-1"><Briefcase size={9} weight="duotone" /> recruiter</div>
                                ) : (
                                    <div className="text-[10px] text-cyan-400">{user.karma} karma</div>
                                )}
                            </div>
                        </Link>
                        <div className="w-px h-6 bg-white/10 mx-0.5 hidden sm:block" />
                        <Link to="/settings" className="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-white/5 shrink-0 transition-colors" data-testid="settings-link" title="Settings" aria-label="Settings">
                            <Gear size={16} weight="duotone" />
                        </Link>
                        <button onClick={logout} aria-label="Log out" title="Log out" className="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-white/5 shrink-0 transition-colors" data-testid="logout-btn">
                            <SignOut size={16} weight="duotone" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 shrink-0">
                        <Link to="/login" data-testid="login-link" aria-label="Sign in"
                              className="shrink-0 hidden md:inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-slate-700 hover:border-cyan-500/50 text-white text-xs uppercase tracking-wider whitespace-nowrap transition-colors">
                            <SignIn size={14} /> {t("common.sign_in")}
                        </Link>
                        <PublicNavMobile />
                    </div>
                )}
            </div>
            {!user && <PublicNavTabletRow />}
            {(!user || user.account_type !== "recruiter") && <CommandPalette member={!!user} />}
            <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
        </header>
    );
}

